import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Alert,
  CircularProgress,
} from "@mui/material";
import { CalendarMonth as CalendarMonthIcon } from "@mui/icons-material";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const HolidayForm = ({ onHolidayCreated = () => {} }) => {
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess("");

    try {
      const token = sessionStorage.getItem("token"); 
      const response = await fetch(`${BASE_URL}/api/holidays/holidays`, {
        method: "POST", 
        headers: {
          Authorization: `Bearer ${token}`, 
          "Content-Type": "application/json", 
        },
        body: JSON.stringify({ name, date }), 
      });

      if (response.ok) {
        if (response.status === 201) {
          setSuccess("Holiday created successfully!");
          setName("");
          setDate("");
          onHolidayCreated();
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Error creating holiday");
      }
    } catch (err) {
      console.error("Error creating holiday:", err);
      setError("Error creating holiday");
    }

    setLoading(false);
  };

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Add New Holiday"
        subtitle="Create a new company holiday"
        icon={<CalendarMonthIcon />}
      />

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #eef0f6",
          borderRadius: "16px",
          p: { xs: 3, sm: 4 },
          maxWidth: 560,
        }}
      >
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 3, borderRadius: "8px" }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: "8px" }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box>
            <Typography
              component="label"
              htmlFor="holiday-name"
              sx={{
                fontWeight: 600,
                fontSize: "0.875rem",
                color: "#1a1a2e",
                mb: 0.75,
                display: "block",
              }}
            >
              Holiday Name
            </Typography>
            <TextField
              id="holiday-name"
              placeholder="e.g. Independence Day"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  bgcolor: "#FFFFFF",
                  "& fieldset": { borderColor: "#eef0f6" },
                  "&:hover fieldset": { borderColor: "#CBD5E1" },
                  "&.Mui-focused fieldset": { borderColor: "#667eea" },
                },
              }}
            />
          </Box>

          <Box>
            <Typography
              component="label"
              htmlFor="holiday-date"
              sx={{
                fontWeight: 600,
                fontSize: "0.875rem",
                color: "#1a1a2e",
                mb: 0.75,
                display: "block",
              }}
            >
              Holiday Date
            </Typography>
            <TextField
              id="holiday-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  bgcolor: "#FFFFFF",
                  "& fieldset": { borderColor: "#eef0f6" },
                  "&:hover fieldset": { borderColor: "#CBD5E1" },
                  "&.Mui-focused fieldset": { borderColor: "#667eea" },
                },
              }}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={18} sx={{ color: "#FFFFFF" }} />
              ) : null
            }
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "8px",
              py: 1.25,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              boxShadow: "none",
              alignSelf: "flex-start",
              px: 4,
              "&:hover": {
                background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                bgcolor: "#93A8D4",
                color: "#FFFFFF",
              },
            }}
          >
            {loading ? "Creating..." : "Create Holiday"}
          </Button>
        </Box>
      </Paper>
    </PageContainer>
  );
};

export default HolidayForm;
